/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { PublishSlotType } from 'src/luis/types/PublishSlotType';
import { useAssignedAzureAccounts } from 'src/modules/azure/hooks/useAssignedAzureAccounts';

export type EndpointDebugSettingsModalProps = BasicModalProps<EndpointDebugSettingsModalDto> & {
    /**
     * The key to use
     */
    selectedCsAccountKeyString: string;

    /**
     * The currently active slot to use in testing
     * the publish endpoint.
     */
    selectedSlot: PublishSlotType;
};

export type EndpointDebugSettingsModalDto = {
    /**
     * The selected endpoint key.
     */
    slot: PublishSlotType;

    /**
     * The bing spell check key from azure.
     */
    subscriptionKey: string;
};

const messages = defineMessages({
    debugModalTitle: {
        id: 'testPane.endpointBlade.debugModalTitle',
        defaultMessage: 'Endpoint test pane settings'
    },
    note: {
        id: 'testPane.endpointBlade.debugModalNote',
        defaultMessage: '* Note: These settings are applied per session and are not saved.'
    },
    keysDropdownLabel: {
        id: 'testPane.endpointBlade.keysDropdownLabel',
        defaultMessage: 'Prediction resource to use'
    },
    publishSlotLabel: {
        id: 'testPane.endpointBlade.publishSlotLabel',
        defaultMessage: 'Publish slot'
    }
});

/**
 * Displays the modal of changing the debug settings.
 */
export const EndpointDebugSettingsModal = withModalMounter(
    observer((props: EndpointDebugSettingsModalProps) => {
        const { selectedCsAccountKeyString, selectedSlot, onSubmit } = props;
        const { localize } = useLocalization();
        const { assignedAzureAccounts: csAccounts, isLoading: areAccountsLoading } = useAssignedAzureAccounts();
        const { formState, updateFormState, formStateDto } = useFormState<EndpointDebugSettingsModalDto>({
            slot: [selectedSlot],
            subscriptionKey: [selectedCsAccountKeyString]
        });

        return (
            <BasicModal
                {...props}
                valid={formState.form.valid}
                onSubmit={() => onSubmit(formStateDto)}
                dialogContentProps={{ title: localize(messages.debugModalTitle) }}>
                <Stack tokens={{ childrenGap: 20 }}>
                    <LuisText>{localize(messages.note)}</LuisText>

                    <LuisDropdown
                        isSearchEnabled={false}
                        label={localize(messages.publishSlotLabel)}
                        selectedKey={formState.fields.slot.value}
                        options={[
                            { key: 'staging', text: 'Staging' },
                            { key: 'production', text: 'Production' }
                        ]}
                        onChange={(_, slot) => updateFormState({ slot: slot.key as PublishSlotType })}
                    />

                    <LuisDropdown
                        enableEmptyState
                        isLoading={areAccountsLoading}
                        label={localize(messages.keysDropdownLabel)}
                        selectedKey={formState.fields.subscriptionKey.value}
                        options={csAccounts.map<IDropdownOption>(a => ({ key: a.keyStrings.key1, text: a.name }))}
                        onChange={(_, selectedKey) => updateFormState({ subscriptionKey: selectedKey.key as string })}
                    />
                </Stack>
            </BasicModal>
        );
    })
);
